import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

export function Navigation() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isDinoWalking, setIsDinoWalking] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const isHomePage = location.pathname === '/';

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // If on home page, scroll smoothly; otherwise navigate to home with hash
  const handleNavClick = (id: string) => {
    if (isHomePage) {
      const element = document.getElementById(id);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    } else {
      navigate(`/#${id}`);
    }
  };

  // After navigating to home with a hash, scroll to the section
  useEffect(() => {
    if (isHomePage && location.hash) {
      const id = location.hash.replace('#', '');
      const tryScroll = (attempts = 0) => {
        const element = document.getElementById(id);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        } else if (attempts < 10) {
          setTimeout(() => tryScroll(attempts + 1), 100);
        }
      };
      tryScroll();
    }
  }, [isHomePage, location.hash]);

  const triggerEasterEgg = () => {
    setIsDinoWalking(false);
    requestAnimationFrame(() => setIsDinoWalking(true));
  };

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-white shadow-md' : 'bg-white/95 backdrop-blur-sm'
      }`}
    >
      <style>{`
        :root{
          --teal:#00BFA5;
          --navy:#1A2332;
        }

        @keyframes dinoWalkFast {
          0%   { left: -18px; }
          100% { left: calc(100% + 12px); }
        }

        @keyframes dinoBobFast {
          0%, 100% { transform: translateY(-50%) translateY(0); }
          50%      { transform: translateY(-50%) translateY(-3px); }
        }

        @keyframes dustPop {
          0%   { opacity: 0; transform: scale(0.5); }
          30%  { opacity: 1; }
          100% { opacity: 0; transform: scale(1.2); }
        }

        .dino-easter-egg {
          animation:
            dinoWalkFast 1.8s steps(44, end) 1 forwards,
            dinoBobFast 0.2s steps(2, end) infinite;
        }

        .dino-parked {
          left: calc(100% + 12px);
          transform: translateY(-50%);
        }

        .dust {
          position: absolute;
          bottom: -2px;
          left: 6px;
          width: 3px;
          height: 3px;
          background: #00BFA5;
          opacity: 0;
          animation: dustPop 0.4s ease-out;
        }

        @media (prefers-reduced-motion: reduce) {
          .dino-easter-egg {
            animation: none !important;
            left: calc(100% + 12px) !important;
            transform: translateY(-50%) !important;
          }
        }
      `}</style>
      
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          <div className="flex items-center">
            <button
              onClick={() => handleNavClick('home')}
              onMouseEnter={triggerEasterEgg}
              onFocus={triggerEasterEgg}
              className="relative text-[#00BFA5] font-nunito text-2xl lg:text-3xl font-extrabold tracking-tight hover:opacity-80 transition-opacity duration-200 cursor-pointer overflow-visible"
            >
              <span className="relative z-10">Dino Nuggets</span>
              
              <div 
                className={`absolute top-1/2 z-20 pointer-events-none ${isDinoWalking ? "dino-easter-egg" : "dino-parked"}`}
                style={{ marginTop: '-2px' }}
                onAnimationEnd={(e) => {
                  if (e.animationName === 'dinoWalkFast') {
                    setIsDinoWalking(false);
                  }
                }}
              >
                <img 
                  src="/readdy-assets/static-a426b8768ce0d209404c4902caff28c8.png"
                  alt="Dino"
                  className="w-8 h-8 object-contain"
                  style={{ imageRendering: 'pixelated' }}
                />
                {isDinoWalking && <span className="dust" style={{ left: '8px' }} />}
                {isDinoWalking && <span className="dust" style={{ left: '18px', animationDelay: '0.1s' }} />}
              </div>
            </button>
          </div>

          <div className="hidden md:flex items-center space-x-8">
            <button
              onClick={() => handleNavClick('home')}
              className="text-secondary hover:text-primary transition-colors duration-200 font-medium cursor-pointer"
            >
              Home
            </button>
            <button
              onClick={() => handleNavClick('services')}
              className="text-secondary hover:text-primary transition-colors duration-200 font-medium cursor-pointer"
            >
              Services
            </button>
            <button
              onClick={() => handleNavClick('pricing')}
              className="text-secondary hover:text-primary transition-colors duration-200 font-medium cursor-pointer"
            >
              Pricing
            </button>
            <button
              onClick={() => handleNavClick('about')}
              className="text-secondary hover:text-primary transition-colors duration-200 font-medium cursor-pointer"
            >
              About
            </button>
            <button
              onClick={() => handleNavClick('contact-details')}
              className="text-secondary hover:text-primary transition-colors duration-200 font-medium cursor-pointer"
            >
              Contact
            </button>
          </div>

          <div className="flex items-center gap-3">
            <a
              href="https://www.facebook.com/profile.php?id=61580588333317"
              target="_blank"
              rel="noopener noreferrer"
              className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors duration-200 cursor-pointer"
              aria-label="Facebook"
            >
              <i className="ri-facebook-fill text-[#00BFA5] text-lg" />
            </a>
            <a
              href="https://www.instagram.com/dinonuggetsai"
              target="_blank"
              rel="noopener noreferrer"
              className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors duration-200 cursor-pointer"
              aria-label="Instagram"
            >
              <i className="ri-instagram-line text-[#00BFA5] text-lg" />
            </a>
            <button
              onClick={() => handleNavClick('cta')}
              className="bg-primary hover:bg-primary/90 text-white px-6 py-2.5 rounded-full font-semibold transition-all duration-200 hover:scale-105 whitespace-nowrap cursor-pointer"
            >
              Get Started
            </button>
          </div>
        </div>
      </div>
    </nav>
  );
}
